'use client';
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import JobCard from './JobCard';
import Link from 'next/link';
import { Spinner } from '@material-tailwind/react';
import { callServer } from '@/functions/api';
import { motion } from 'framer-motion';
import { setJobRolesInSession } from '@/functions/utils';

function CareerPageContent() {
    const [jobOpenings, setJobOpenings] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [filteredOpenings, setFilteredOpenings] = useState(jobOpenings);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [token, setToken] = useState(null);

    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            return;
        }

        const token = await executeRecaptcha('page_view');
        setToken(token);
    }, [executeRecaptcha]);

    useEffect(() => {
        handleReCaptchaVerify();
    }, [handleReCaptchaVerify]);

    useEffect(() => {
        async function f() {
            if (!token) {
                return;
            }

            const jobOpeningData = await callServer(
                `get_job_openings?token=${token}`,
            );
            if (jobOpeningData.message) {
                const filteredData = jobOpeningData.message.data.filter(
                    (job) => job.status !== 'Closed',
                );
                setJobOpenings(filteredData);
                setFilteredOpenings(filteredData);
                setJobRolesInSession(
                    filteredData ? filteredData.map((i) => i.name) : {},
                );
            }
        }
        f();
    }, [token]);

    useEffect(() => {
        if (!jobOpenings) {
            return;
        }

        let filteredData = jobOpenings;

        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            filteredData = filteredData.filter((job) =>
                job.job_title.toLowerCase().includes(term),
            );
        }

        if (departmentFilter) {
            filteredData = filteredData.filter(
                (job) => job.department === departmentFilter,
            );
        }

        setFilteredOpenings(filteredData);
    }, [jobOpenings, searchTerm, departmentFilter]);

    const departments = jobOpenings
        ? jobOpenings
              .map((job) => job.department)
              .filter(
                  (department, index, all) =>
                      Boolean(department) && all.indexOf(department) === index,
              )
        : [];

    return (
        <div className="pb-8">
            <div
                id="page-1"
                className="h-full bg-opacity-80 mb-16 sm:mb-24 md:mb-32"
            >
                <Image
                    width={1000}
                    height={100}
                    src="/images/hiring.png"
                    autoPlay
                    muted
                    loop
                    alt="Image"
                    className="md:hidden mt-6 mx-auto w-[85%] sm:w-[70%] px-4"
                    id="top-image"
                />
                <div id="content">
                    <div className="w-full max-h-screen md:flex flex-col md:flex-row mt-0 md:mt-[-5vw]">
                        <div
                            id="left"
                            className="w-full md:w-1/2 md:h-full px-4 sm:px-6 md:pl-36 py-6 sm:py-8 md:py-12 md:ml-10 md:pt-32"
                        >
                            <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-blue-600 bg-blue-50 rounded-full px-4 py-1.5">
                                <i className="ri-sparkling-2-line"></i>
                                We&apos;re Hiring
                            </span>
                            <h1
                                className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-extrabold mt-4 md:mt-[2vw] w-full text-gray-800 leading-tight"
                                id="maintext"
                            >
                                Unlock Your Potential with{' '}
                                <span className="bg-gradient-to-r from-[#4695F0] to-[#57CCF9] bg-clip-text text-transparent">
                                    Our Thriving Team
                                </span>
                            </h1>
                            <p className="text-sm sm:text-base md:text-md text-gray-600 pt-4 sm:pt-6 md:pt-8 leading-relaxed">
                                We&apos;re not your average company, and neither
                                should your career be. Here, diversity thrives,
                                ideas ignite, and boundaries blur. We&apos;re
                                all about pushing limits, celebrating
                                differences, and building something
                                extraordinary together.
                            </p>

                            <p className="pt-3 sm:pt-4 text-sm sm:text-base md:text-md font-medium text-gray-800 flex items-center gap-2 flex-wrap">
                                <i className="ri-phone-line text-blue-500"></i>
                                For Career and HR enquiries, reach us at
                                <a
                                    href="tel:+918086651555"
                                    className="text-blue-600 font-semibold"
                                >
                                    +91 80866 51555
                                </a>
                            </p>

                            <Link rel="canonical" href="#jobList">
                                <button
                                    id="getquote"
                                    className="w-full sm:w-auto flex items-center justify-center gap-2 py-3 px-10 sm:py-[2vw] sm:px-[17vw] md:py-[0.7vw] md:px-[2vw] bg-gradient-to-r from-[#4695F0] to-[#57CCF9] rounded-md mt-6 md:mt-[2vw] shadow-xl hover:shadow-lg transition duration-300 md:ml-[0vw]"
                                >
                                    <span
                                        className="font-bold sm:font-extrabold md:font-semibold text-[#f1f2f3] text-sm sm:text-base"
                                        id="getquote2"
                                    >
                                        Apply Now
                                    </span>
                                    <i className="ri-arrow-right-line text-[#f1f2f3]"></i>
                                </button>
                            </Link>
                        </div>
                        <div
                            id="right"
                            className="hidden md:block w-[60%] h-full bg-transparent object-center md:pt-20"
                        >
                            <Image
                                src="/images/hiring.png"
                                autoPlay
                                loop
                                muted
                                alt="Image"
                                width={1000}
                                height={100}
                                className="w-[100%] mt-14 pr-[10vw] transition-all delay-200 hover:scale-110"
                                id="bottom-Image"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div
                id="jobList"
                className="px-4 sm:px-8 md:px-20 py-6 sm:py-8 mx-0 sm:mx-4 md:mx-24"
            >
                <div className="flex items-center gap-3 mb-6">
                    <span className="inline-flex items-center justify-center h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-gradient-to-br from-[#4695F0] to-[#57CCF9] shadow-md shadow-blue-100 shrink-0">
                        <i className="ri-briefcase-4-line text-xl sm:text-2xl text-white"></i>
                    </span>
                    <div>
                        <p
                            id="career-section"
                            className="text-2xl sm:text-3xl font-bold text-gray-800"
                        >
                            Job Openings
                        </p>
                        {jobOpenings && (
                            <p className="text-sm text-gray-500 font-medium">
                                {filteredOpenings ? filteredOpenings.length : 0}{' '}
                                of {jobOpenings.length}{' '}
                                {jobOpenings.length === 1
                                    ? 'position'
                                    : 'positions'}{' '}
                                shown
                            </p>
                        )}
                    </div>
                </div>

                <div className="rounded-2xl border border-gray-100 shadow-sm bg-white p-4 sm:p-5 flex flex-col gap-4">
                    <div className="relative">
                        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search job title..."
                            className="w-full rounded-lg border border-gray-200 pl-11 pr-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
                        />
                    </div>

                    {departments.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setDepartmentFilter(null)}
                                className={`inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold rounded-full px-4 py-1.5 transition-colors ${
                                    !departmentFilter
                                        ? 'bg-gradient-to-r from-[#4695F0] to-[#57CCF9] text-white shadow-sm'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                            >
                                <i className="ri-apps-2-line"></i>
                                All Departments
                            </button>
                            {departments.map((department) => (
                                <button
                                    key={department}
                                    type="button"
                                    onClick={() =>
                                        setDepartmentFilter(department)
                                    }
                                    className={`inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold rounded-full px-4 py-1.5 transition-colors ${
                                        departmentFilter === department
                                            ? 'bg-gradient-to-r from-[#4695F0] to-[#57CCF9] text-white shadow-sm'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    <i className="ri-building-4-line"></i>
                                    {department}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {filteredOpenings && filteredOpenings.length > 0 && (
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.05 }}
                    variants={{
                        hidden: {},
                        visible: {
                            transition: { staggerChildren: 0.08 },
                        },
                    }}
                    className="flex flex-col gap-4 sm:gap-5 px-4 sm:px-8 mx-0 sm:mx-4 md:mx-24"
                >
                    {filteredOpenings.map((i) => (
                        <JobCard
                            key={i.name}
                            name={i.name}
                            jobTitle={i.job_title}
                            department={i.department}
                            designation={i.designation}
                            jobDescription={i.description}
                        />
                    ))}
                </motion.div>
            )}

            {filteredOpenings && filteredOpenings.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-3 py-16 px-4 text-center">
                    <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-blue-50">
                        <i className="ri-search-eye-line text-2xl text-blue-400"></i>
                    </span>
                    <p className="text-lg font-semibold text-gray-700">
                        No matching roles right now
                    </p>
                    <p className="text-sm text-gray-500">
                        Try a different job title, or check back soon for new
                        openings.
                    </p>
                </div>
            )}

            {!filteredOpenings ? (
                <div className="flex flex-col items-center justify-center gap-3 mt-8 sm:mt-10">
                    <Spinner
                        className="h-10 w-10 sm:h-12 sm:w-12"
                        color="blue"
                    />
                    <p className="text-sm text-gray-500 font-medium">
                        Fetching the latest openings...
                    </p>
                </div>
            ) : null}
        </div>
    );
}

export default function Page() {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey="6Ld-QI0iAAAAAPW9CwclXHZYoX0H0QQLhqTuc2iQ"
            scriptProps={{
                async: true,
                defer: true,
                appendTo: 'head',
                nonce: undefined,
            }}
        >
            <CareerPageContent />
        </GoogleReCaptchaProvider>
    );
}
